import {
    BlurOff,
    BlurOn,
    Map as MapIcon,
    MapOutlined,
    Visibility,
    VisibilityOff,
} from '@mui/icons-material'
import { Badge, Box, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useTranslate } from '@tolgee/react'
import {
    ButtonWithDropdown,
    ErrorIcon,
    FilterIcon,
    Header,
    useMinimalsTheme,
    WarningIcon,
} from '@tom-ui/ui'
import { useYardStores } from '@yard/AppProvider'
import { getYardCapacity } from '@yard/utils/allocation-rule-capacity.util'
import { observer } from 'mobx-react-lite'
import { YARD_DRAWERS_WIDTH } from '../../helpers/yard-view.utils'
import { useUnsavedChangesGuard } from '../../hooks/use-unsaved-changes-guard'
import { CarrierVisitAllocationRulesViewStore } from '../../stores/allocation-rules/carrier-visit-allocation-rules.view-store'
import { GlobalAllocationRulesViewStore } from '../../stores/allocation-rules/global-allocation-rules.view-store'
import { CarrierVisitOverviewViewStore } from '../../stores/carrier-visit-overview.viewstore'
import { HousekeepingPlanAccumulatorViewStore } from '../../stores/housekeeping-plan-accumulator.view-store'
import { YardViewStore } from '../../stores/yard.view-store'
import { BackgroundModeSelector } from '../atoms/BackgroundModeSelector'
import { CapacityTooltipContent } from '../atoms/CapacityTooltipContent'
import { UnsavedChangesDialog } from '../molecules/UnsavedChangesDialog'
import { CarrierVisitAllocationRulesActions } from './AllocationRules/CarrierVisitAllocationRules/CarrierVisitAllocationRulesActions'
import { GlobalAllocationRulesActions } from './AllocationRules/GlobalAllocationRules/GlobalAllocationRulesActions'

interface Props {
  yardViewStore: YardViewStore
  carrierVisitOverviewViewStore: CarrierVisitOverviewViewStore
  globalAllocationRulesViewStore: GlobalAllocationRulesViewStore
  carrierVisitAllocationRulesViewStore: CarrierVisitAllocationRulesViewStore
  housekeepingPlanAccumulatorViewStore: HousekeepingPlanAccumulatorViewStore
}
export const YardViewHeader = observer((props: Props) => {
  const {
    yardViewStore,
    carrierVisitOverviewViewStore,
    globalAllocationRulesViewStore,
    carrierVisitAllocationRulesViewStore,
    housekeepingPlanAccumulatorViewStore,
  } = props
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const housekeepingEnabled = useBooleanFlagValue('house-keeping', false)
  const { showUnsavedChangesDialog, attemptAction, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard()
  const { yardBlockStore, occupancyStore, capacitySettingsStore } = useYardStores()

  const yardWarningThreshold = capacitySettingsStore.yardWarningThreshold
  const yardCapacity = getYardCapacity(
    yardBlockStore.items,
    occupancyStore.occupanciesByBlock,
    yardWarningThreshold,
  )
  const isYardOvercapacity = yardCapacity.isOvercapacity
  const isYardWarning = yardCapacity.isWarning

  const yardOverCapacityLabel = t('yardCapacityFull', 'Yard full')
  const yardWarningLabel = t('yardCapacityWarning', 'Yard approaching capacity')

  const openGlobalRules = () => {
    if (globalAllocationRulesViewStore.isAllocationRulesDrawerOpen) return

    const hasUnsaved = carrierVisitAllocationRulesViewStore.hasUnsavedChanges

    const action = () => {
      if (carrierVisitAllocationRulesViewStore.hasUnsavedChanges) {
        carrierVisitAllocationRulesViewStore.resetChanges()
      }
      carrierVisitAllocationRulesViewStore.setAllocationRulesDrawerOpen(false)
      carrierVisitOverviewViewStore.setOverviewOpen(false)
      housekeepingPlanAccumulatorViewStore.deactivate()

      globalAllocationRulesViewStore.setAllocationRulesDrawerOpen(true)
      yardViewStore.setShowAllocationRulesPreview(true)
    }

    const dispose = () => {
      carrierVisitAllocationRulesViewStore.resetChanges()
    }

    attemptAction(hasUnsaved, action, dispose)
  }

  const openCarrierVisitOverview = () => {
    if (carrierVisitOverviewViewStore.isOverviewOpen) return

    const hasUnsaved =
      globalAllocationRulesViewStore.hasUnsavedChanges ||
      carrierVisitAllocationRulesViewStore.hasUnsavedChanges

    const action = () => {
      if (globalAllocationRulesViewStore.hasUnsavedChanges) {
        globalAllocationRulesViewStore.resetChanges()
      }
      if (carrierVisitAllocationRulesViewStore.hasUnsavedChanges) {
        carrierVisitAllocationRulesViewStore.resetChanges()
      }

      globalAllocationRulesViewStore.setAllocationRulesDrawerOpen(false)
      carrierVisitAllocationRulesViewStore.setAllocationRulesDrawerOpen(false)
      housekeepingPlanAccumulatorViewStore.deactivate()
      yardViewStore.setShowAllocationRulesPreview(true)

      carrierVisitOverviewViewStore.setOverviewOpen(true)
    }

    const dispose = () => {
      globalAllocationRulesViewStore.resetChanges()
      carrierVisitAllocationRulesViewStore.resetChanges()
    }

    attemptAction(hasUnsaved, action, dispose)
  }

  const openHousekeepingPlan = () => {
    // Toggle: clicking the dropdown entry while open closes the accumulator.
    // The drawer is `variant='persistent'` (no backdrop) and the accumulator
    // UI doesn't render its own close button, so this dropdown click is the
    // only path back out.
    if (housekeepingPlanAccumulatorViewStore.isAccumulatorOpen) {
      housekeepingPlanAccumulatorViewStore.deactivate()
      return
    }

    const hasUnsaved =
      globalAllocationRulesViewStore.hasUnsavedChanges ||
      carrierVisitAllocationRulesViewStore.hasUnsavedChanges

    const action = () => {
      if (globalAllocationRulesViewStore.hasUnsavedChanges) {
        globalAllocationRulesViewStore.resetChanges()
      }
      if (carrierVisitAllocationRulesViewStore.hasUnsavedChanges) {
        carrierVisitAllocationRulesViewStore.resetChanges()
      }

      globalAllocationRulesViewStore.setAllocationRulesDrawerOpen(false)
      carrierVisitAllocationRulesViewStore.setAllocationRulesDrawerOpen(false)
      carrierVisitOverviewViewStore.setOverviewOpen(false)

      housekeepingPlanAccumulatorViewStore.activate()
    }

    const dispose = () => {
      globalAllocationRulesViewStore.resetChanges()
      carrierVisitAllocationRulesViewStore.resetChanges()
    }

    attemptAction(hasUnsaved, action, dispose)
  }

  const rightRenderOption = globalAllocationRulesViewStore.isAllocationRulesDrawerOpen ? (
    <GlobalAllocationRulesActions {...props} />
  ) : carrierVisitAllocationRulesViewStore.isAllocationRulesDrawerOpen ? (
    <CarrierVisitAllocationRulesActions {...props} />
  ) : null

  const isLeftDrawerOpen =
    globalAllocationRulesViewStore.isAllocationRulesDrawerOpen ||
    carrierVisitAllocationRulesViewStore.isAllocationRulesDrawerOpen ||
    carrierVisitOverviewViewStore.isOverviewOpen ||
    housekeepingPlanAccumulatorViewStore.isAccumulatorOpen

  return (
    <Stack position='absolute' width='100%'>
      <Header
        leftRenderOption={
          <Stack direction='row' spacing={1} alignItems='center' width='100%'>
            <Typography variant='h2'>{t('yard', 'Yard')}</Typography>

            <ButtonWithDropdown
              testId='plan-yard-rules-btn'
              label={t('plan', 'Plan')}
              color='inherit'
              variant='text'
              options={[
                {
                  label: t('carrierVisit', 'carrier visit'),
                  onClick: () => {
                    openCarrierVisitOverview()
                  },
                },
                {
                  label: t('globalRules', 'global rules'),
                  onClick: () => openGlobalRules(),
                },
                ...(housekeepingEnabled
                  ? [
                      {
                        label: t('housekeepingPlan', 'Housekeeping plan'),
                        onClick: () => openHousekeepingPlan(),
                      },
                    ]
                  : []),
              ]}
            />
          </Stack>
        }
        rightRenderOption={rightRenderOption}
      />

      <Stack
        sx={{
          position: 'relative',
          zIndex: theme.zIndex.appBar,
          gap: theme.customSpacing.m,
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <Stack
          sx={{
            position: 'relative',
            top: theme.customSpacing.s,
            left: `calc(${isLeftDrawerOpen ? YARD_DRAWERS_WIDTH : 0}px + ${theme.customSpacing.s})`,
            transition: 'left 225ms cubic-bezier(0, 0, 0.2, 1)',
            zIndex: theme.zIndex.appBar,
            gap: theme.customSpacing.m,
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Paper elevation={4} sx={{ width: 'fit-content', position: 'relative' }}>
            <Badge
              color='primary'
              overlap='circular'
              badgeContent={yardViewStore.filterCount}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{ '& .MuiBadge-badge': { right: 0 } }}
            >
              <Stack direction='row' spacing={1} alignItems='center'>
                <Tooltip title={t('filter', 'Filter')}>
                  <IconButton
                    onClick={() =>
                      yardViewStore.setFilterDrawerOpen(!yardViewStore.isFilterDrawerOpen)
                    }
                  >
                    <FilterIcon color={yardViewStore.filterCount ? 'primary' : 'inherit'} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Badge>
          </Paper>

          <Paper elevation={4} sx={{ width: 'fit-content', position: 'relative' }}>
            <Stack direction='row' alignItems='center'>
              <Tooltip title={t('allocationRulesPreview', 'Allocation rules preview')}>
                <IconButton
                  onClick={() =>
                    yardViewStore.setShowAllocationRulesPreview(
                      !yardViewStore.showAllocationRulesPreview,
                    )
                  }
                >
                  {yardViewStore.showAllocationRulesPreview ? <BlurOn /> : <BlurOff />}
                </IconButton>
              </Tooltip>

              <Tooltip title={t('legend', 'Legend')}>
                <IconButton onClick={() => yardViewStore.setShowLegend(!yardViewStore.showLegend)}>
                  {yardViewStore.showLegend ? <MapIcon /> : <MapOutlined />}
                </IconButton>
              </Tooltip>

              <Tooltip title={t('toggleNodeHeaders', 'Toggle node headers')}>
                <IconButton
                  onClick={() => yardViewStore.setShowNodeHeaders(!yardViewStore.showNodeHeaders)}
                >
                  {yardViewStore.showNodeHeaders ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>

          <Paper elevation={4} sx={{ width: 'fit-content', position: 'relative' }}>
            <BackgroundModeSelector yardViewStore={yardViewStore} />
          </Paper>

          {isYardOvercapacity && (
            <Paper elevation={4} sx={{ width: 'fit-content', position: 'relative' }}>
              <Stack direction='row' alignItems='center'>
                <Tooltip
                  title={
                    <CapacityTooltipContent
                      headline={yardOverCapacityLabel}
                      utilizationPct={yardCapacity.utilizationPct}
                      consumedTeu={yardCapacity.consumedTeu}
                      totalTeu={yardCapacity.totalTeu}
                    />
                  }
                >
                  <Box
                    component='span'
                    data-cy='yard-overcapacity-indicator'
                    sx={{ display: 'inline-flex', p: 1 }}
                  >
                    <ErrorIcon color='error' aria-label={yardOverCapacityLabel} />
                  </Box>
                </Tooltip>
              </Stack>
            </Paper>
          )}

          {isYardWarning && !isYardOvercapacity && (
            <Paper elevation={4} sx={{ width: 'fit-content', position: 'relative' }}>
              <Stack direction='row' alignItems='center'>
                <Tooltip
                  title={
                    <CapacityTooltipContent
                      headline={yardWarningLabel}
                      utilizationPct={yardCapacity.utilizationPct}
                      consumedTeu={yardCapacity.consumedTeu}
                      totalTeu={yardCapacity.totalTeu}
                    />
                  }
                >
                  <Box
                    component='span'
                    data-cy='yard-warning-indicator'
                    sx={{ display: 'inline-flex', p: 1 }}
                  >
                    <WarningIcon color='warning' aria-label={yardWarningLabel} />
                  </Box>
                </Tooltip>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Stack>

      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </Stack>
  )
})
